import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Dropdown,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
// contexts
import { useUser } from "contexts/UserContext";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
// utils
import queryBusinessAnalyticsInfo from "services/queryBusinessAnalyticsInfo";
import getClasses from "utils/getClasses";
import { disableStripeFeatures } from "utils/restrictAccess";
import getChartData from "./utils/getChartData";
import getTotalEarnings from "./utils/getTotalEarnings";
import getXTicksByType from "./utils/getXTicksByType";
// components
import { ReactComponent as BlueEllipse } from "assets/svg/blue-ellipse.svg";
import { ReactComponent as DataAnalyticsEmptyState } from "assets/svg/data-analytics-empty-state.svg";
import { ReactComponent as EarningLegendIcon } from "assets/svg/earning-legend.svg";
import { ReactComponent as GreenEllipse } from "assets/svg/green-ellipse.svg";
import { ReactComponent as OutlineArrowRightCircle } from "assets/svg/outline-arrow-right-circle.svg";
import { ReactComponent as Previous } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as Next } from "assets/svg/outline-chevron-right-16.svg";
import { ReactComponent as OutlineInfoCircle16 } from "assets/svg/outline-info-circle-16.svg";
import { ReactComponent as UtilCoins } from "assets/svg/uil-coins.svg";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import IconButton from "components/icon-button";
import ConnectWithStripe from "../components/connect-with-stripe";
import SessionsOutlook from "../components/sessions-outlook";
import BABarChart from "./components/ba-bar-chart";
// constants
import loginToStripe from "services/loginToStripe";
import { AmplitudePageName, track } from "utils/amplitude";
import {
  EARNINGS_DATA_KEYS,
  MONTH_NAMES,
  TIME_FILTER_OPTIONS,
  TTimeFilterType,
} from "./constants";

// styles
import styles from "./styles.module.scss";

const ANALYTICS_COMPONENT_NAME = "analytics-component";

// TODO: use UTC for now
const nowYear = new Date().getUTCFullYear();
const nowMonthIndex = new Date().getUTCMonth();

const isNoDataForCurrMonth = (year: number, monthIndex: number) => {
  const firstDateCurrMonth = new Date(year, monthIndex, 1);
  const firstDayCurrMonth = firstDateCurrMonth.getDay();
  // check if first day of currMonth starts from Sunday
  if (firstDayCurrMonth !== 0) {
    const date = new Date(firstDateCurrMonth);
    date.setDate(date.getDate() + (7 - firstDayCurrMonth));
    const startDayCurrMonth = new Date(date).getDate();
    const todayDay = new Date().getDate();
    if (todayDay > startDayCurrMonth) {
      return false; // data exists
    } else {
      return true;
    }
  } else {
    return false; // data exists
  }
};

interface Props {
  analyticsPageName: AmplitudePageName;
}

const BusinessAnalytics: React.FC<Props> = ({ analyticsPageName }) => {
  const { user } = useUser();
  const { isPhonePortrait } = usePhonePortraitContext();
  const [error, setError] = useState<any>();
  const [type, setType] = useState<TTimeFilterType>(
    TIME_FILTER_OPTIONS.year.id,
  );
  const [businessInfo, setBusinessInfo] = useState<DataDashBoardData>({
    patients: 0,
    sessions_completed: 0,
    upcoming_sessions: 0,
    cancelled_sessions: 0,
    earnings_days: [],
    earnings_weeks: [],
    earnings_months: [],
    next_payout: "",
    prev_payout: "",
    orchid_discount_this_month: 0,
  });

  // const [earningsData, setEarningsData] = useState<EarningsEntry[]>([]);
  const [year, setYear] = useState(nowYear);
  const [monthIndex, setMonthIndex] = useState(nowMonthIndex);

  // get business analytics data on init
  useEffect(() => {
    if (!user?.sub) {
      return;
    }
    queryBusinessAnalyticsInfo()
      .then((res) => {
        setBusinessInfo(res);
      })
      .catch((err) => {
        setError(err);
      });
  }, [user?.sub]);

  const handleLoginStripeBtnClick = async () => {
    try {
      const { url } = await loginToStripe();
      !isPhonePortrait
        ? window.open(url, "_blank")
        : window.location.replace(url);
    } catch (error) {
      setError(error);
    }
  };

  const earningsData = useMemo(
    () => businessInfo[EARNINGS_DATA_KEYS[type]],
    [businessInfo, type],
  );

  const onMonthChange = (amount) => {
    setType("month");
    if (amount === -1) {
      if (monthIndex === 0 && year > 2020) {
        setYear(year - 1);
        setMonthIndex(11);
      } else {
        setMonthIndex(monthIndex - 1);
      }
    } else if (amount === 1) {
      if (monthIndex === 11) {
        setYear(year + 1);
        setMonthIndex(0);
      } else {
        setMonthIndex(monthIndex + 1);
      }
    }
  };

  const dateRangeToggle = (type) => {
    switch (type) {
      case "year":
        return (
          <div className="d-flex align-items-center">
            <IconButton
              disabled={year <= 2020}
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "prev-year-button"],
                  "click",
                );
                setYear((s) => s - 1);
              }}
            >
              <Previous />
            </IconButton>

            <div className="mx-2 rg-14 gray01">{year}</div>
            <IconButton
              disabled={year >= nowYear}
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "next-year-button"],
                  "click",
                );
                setYear((s) => s + 1);
              }}
            >
              <Next />
            </IconButton>
          </div>
        );
      case "month":
        return (
          <div className="d-flex align-items-center">
            <IconButton
              disabled={year <= 2020 && monthIndex <= 0}
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "prev-month-button"],
                  "click",
                );
                onMonthChange(-1);
              }}
            >
              <Previous />
            </IconButton>
            <div className="mx-2 rg-14 gray01">
              {year} {MONTH_NAMES[monthIndex]}
            </div>
            <IconButton
              disabled={
                year === nowYear &&
                (monthIndex >= nowMonthIndex ||
                  // if first day of thisMonth doesn't start on Sunday, there will be no data from BE
                  (monthIndex === nowMonthIndex - 1 &&
                    isNoDataForCurrMonth(year, nowMonthIndex)))
              }
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "next-month-button"],
                  "click",
                );

                onMonthChange(1);
              }}
            >
              <Next />
            </IconButton>
          </div>
        );
      default:
        break;
    }
  };

  const timeRangeFilter = useMemo(() => {
    switch (type) {
      case TIME_FILTER_OPTIONS.last_week.id: {
        return {
          min: -Infinity,
          max: Infinity,
        };
      }

      case TIME_FILTER_OPTIONS.month.id: {
        const min = Date.UTC(year, monthIndex, 1, 0, 0, 0, 0);
        const minDate = new Date(min);
        minDate.setUTCDate(minDate.getUTCDate() - minDate.getUTCDay());

        const maxDate = new Date(min);
        maxDate.setUTCMonth(maxDate.getUTCMonth() + 1);
        maxDate.setUTCDate(maxDate.getUTCDate() - 1);
        // utc day 0 for Sunday, 1 for Monday, 2 for Tuesday, and so on.
        maxDate.setUTCDate(maxDate.getUTCDate() + 7 - maxDate.getUTCDay());
        const max = maxDate.valueOf();

        return {
          min: minDate.valueOf(),
          max,
        };
      }

      case TIME_FILTER_OPTIONS.year.id: {
        const max = Date.UTC(year + 1, 0, 1, 0, 0, 0, 0) - 1;

        const min = Date.UTC(year, 0, 1, 0, 0, 0, 0);
        return {
          min,
          max,
        };
      }

      default:
        return { min: 0, max: 0 };
    }
  }, [monthIndex, type, year]);

  const { xTicks, chartData } = useMemo(() => {
    const xTicks = getXTicksByType({ monthIndex, year, type });
    const chartData = getChartData({
      earningsData,
      range: timeRangeFilter,
      xTicks,
    });

    return { xTicks, chartData };
  }, [earningsData, monthIndex, timeRangeFilter, type, year]);

  return (
    <div className={styles["business-analytics"]}>
      <Row className="align-items-start justify-content-between">
        <div>
          <p className="mb-2 bd-18 deep-blue">Analytics</p>
          <p className="mb-0 rg-14 gray01" style={{ maxWidth: "18.75rem" }}>
            Here's a quick overview of your earnings!
          </p>
        </div>

        {user?.has_stripe_account_connected && (
          <Button
            variant="link"
            size="sm"
            disabled={disableStripeFeatures(user)}
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "learn-more-on-stripe-button"],
                "click",
              );
              handleLoginStripeBtnClick();
            }}
          >
            <div className="d-flex align-items-center">
              <div className="mr-2 md-18">Learn more on Stripe</div>
              <OutlineArrowRightCircle />
            </div>
          </Button>
        )}
      </Row>
      {error && (
        <Alert variant="danger" className="mt-3">
          {error.toString()}
        </Alert>
      )}
      {!user?.has_stripe_account_connected ? (
        <div className={styles["business-analytics__empty"]}>
          <DataAnalyticsEmptyState />
          <div className={styles["business-analytics__empty-text"]}>
            Connect with Stripe to start viewing your earnings.
          </div>
          <ConnectWithStripe
            title={`Connect with Stripe`}
            amplitudePageName={analyticsPageName}
            amplitudeComponentName={ANALYTICS_COMPONENT_NAME}
            className="p-btn"
          />
        </div>
      ) : (
        <>
          <Row className={styles["business-analytics__row"]}>
            <Col md={3} className={styles["business-analytics__left"]}>
              <div className={styles["saving-w-orchid__wrapper"]}>
                <div className={styles["saving-w-orchid__content"]}>
                  <div>
                    <UtilCoins />
                    <div className="mt-2 bd-16">Saving with Orchid</div>
                  </div>
                  <div
                    className={styles["saving-w-orchid__content-amount"]}
                  >{`$${(
                    businessInfo.earnings_months[0]?.earnings
                      ?.orchid_discount || 0
                  ).toFixed(2)}`}</div>
                  <div className={styles["saving-w-orchid__month"]}>
                    Saved this month
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip
                          id="button-tooltip"
                          className={styles["saving-w-orchid__content-tooltip"]}
                        >
                          <b>$50</b> is the average amount of fee charged from
                          other platforms. You are free of charge because Orchid
                          covers it for you!
                        </Tooltip>
                      }
                    >
                      <OutlineInfoCircle16 className="ml-1" />
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
              <div className={styles["saving-w-orchid__payout-cycle"]}>
                <div className="d-flex align-items-center">
                  Payout Cycle
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip
                        id="button-tooltip"
                        className={styles["saving-w-orchid__payout-tooltip"]}
                      >
                        Stripe payout cycle is every 2 days.
                      </Tooltip>
                    }
                  >
                    <OutlineInfoCircle16
                      color={`var(--blue)`}
                      className="ml-1"
                    />
                  </OverlayTrigger>
                </div>

                {!businessInfo.next_payout && !businessInfo.prev_payout && (
                  <div className="gray02 d-flex align-items-center">
                    <BlueEllipse className="mr-1" />
                    No transaction yet.
                  </div>
                )}

                {businessInfo.next_payout && (
                  <div className="deep-blue d-flex align-items-center">
                    <GreenEllipse className="mr-1" />
                    Upcoming payout:{" "}
                    {new Date(businessInfo.next_payout).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                  </div>
                )}

                {businessInfo.prev_payout && (
                  <div className="gray02 d-flex align-items-center">
                    <BlueEllipse className="mr-1" />
                    Previous payout:{" "}
                    {new Date(businessInfo.prev_payout).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                  </div>
                )}
              </div>
            </Col>
            <Col md={9} className={styles["business-analytics__right"]}>
              <div className={styles["earnings"]}>
                <Row className={styles["earnings__title"]}>
                  <Col className="p-0">
                    <div className="mb-3 bd-16 gray01 d-flex align-items-center">
                      Earnings
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip
                            id="button-tooltip"
                            className={styles["earnings__tooltip"]}
                          >
                            Total earnings of
                            {type === TIME_FILTER_OPTIONS.last_week.id
                              ? ` the last 7 days`
                              : type === TIME_FILTER_OPTIONS.month.id
                              ? ` the month`
                              : ` the year`}
                          </Tooltip>
                        }
                      >
                        <OutlineInfoCircle16
                          className="ml-1"
                          color={`var(--blue)`}
                        />
                      </OverlayTrigger>
                    </div>
                    <div className="bd-32 deep-blue">
                      {`$${(getTotalEarnings(chartData) || 0).toFixed(2)}`}
                    </div>
                  </Col>
                  <Col className="p-0">
                    <div className="d-flex justify-content-end align-items-center">
                      {dateRangeToggle(type)}
                      {/* change type dropdwon */}
                      <DropdownToggle
                        className={getClasses(
                          "ml-3",
                          styles["earnings__dropdown"],
                        )}
                        caretClassName={styles["earnings__dropdown-caret"]}
                        title={
                          type === "last_week"
                            ? `Last 7 days`
                            : type.charAt(0).toUpperCase() + type.slice(1)
                        }
                      >
                        {Object.values(TIME_FILTER_OPTIONS).map((el) => (
                          <Dropdown.Item
                            key={el.id}
                            onClick={() => {
                              track(
                                analyticsPageName,
                                [
                                  ANALYTICS_COMPONENT_NAME,
                                  "time-filter-option",
                                ],
                                "click",
                              );
                              setType(el.id);
                            }}
                          >
                            {el.label}
                          </Dropdown.Item>
                        ))}
                      </DropdownToggle>
                    </div>
                    <div
                      className="mt-1 ml-auto d-flex align-items-center"
                      style={{ width: "7.5rem" }}
                    >
                      <EarningLegendIcon />
                      <div className={styles["custom-legend"]}>{`Earning per ${
                        type === "year"
                          ? "month"
                          : type === "month"
                          ? "week"
                          : "day"
                      }`}</div>
                    </div>
                  </Col>
                </Row>
                <div className={styles["earnings__bar-chart"]}>
                  <BABarChart
                    type={type}
                    chartData={chartData}
                    xTicks={xTicks}
                  />
                </div>
              </div>
            </Col>
          </Row>

          <div className={styles["breakdowns"]}>
            <div className={styles["breakdowns__header"]}>Breakdowns</div>
            <SessionsOutlook isInDashboardHome={true} />
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessAnalytics;
